"use client";

import React from "react";
import { Label } from "../../label.js";
import { Input } from "../../input.js";
import { Textarea } from "../../textarea.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../select.js";
import { Button } from "../../button.js";
import { Alert, AlertDescription } from "../../alert.js";
import { Video, MapPin, Info, AlertCircle } from "lucide-react";
import { MeetingFormData, CalendarPlatform } from "../types/meeting.js";
import { DURATION_OPTIONS, PLATFORM_OPTIONS, TEAM_MEMBERS } from "../constants/meetingConstants.js";
import { AttendeeChips } from "../attendees/AttendeeChips.js";
import { AttendeeSelector } from "../attendees/AttendeeSelector.js";
import { AttendeeSelectWithModal } from "../attendees/AttendeeSelectWithModal.js";

interface MeetingFormFieldsMobileProps {
  formData: MeetingFormData;
  onInputChange: (field: keyof MeetingFormData, value: string | number | boolean) => void;
  onAddTeamMember: (email: string) => void;
  onRemoveAttendee: (email: string) => void;
  currentAttendees: string[];
  onAttendeesChange: (emails: string[]) => void;
  onOpenAttendeeInfo: () => void;
  isSubmitting: boolean;
  submitError: string | null;
  /** Platforms the user can create on; when omitted all options are shown. */
  availablePlatforms?: CalendarPlatform[];
  renderAttendeesSelector?: (args: {
    currentEmails: string[];
    setEmails: (emails: string[]) => void;
    disabled: boolean;
  }) => React.ReactNode;
  contacts?: Array<{ id: string; name: string; email: string; company_name?: string }>;
  onSearchContacts?: (searchTerm: string) => Promise<Array<{ id: string; name: string; email: string; company_name?: string }>>;
  isLoadingContacts?: boolean;
  ContactPopoverComponent?: React.ComponentType<{ name: string; email: string; children: React.ReactNode }>;
}

export const MeetingFormFieldsMobile: React.FC<MeetingFormFieldsMobileProps> = ({
  formData,
  onInputChange,
  onAddTeamMember,
  onRemoveAttendee,
  currentAttendees,
  onAttendeesChange,
  onOpenAttendeeInfo,
  isSubmitting,
  submitError,
  availablePlatforms,
  renderAttendeesSelector,
  contacts,
  onSearchContacts,
  isLoadingContacts,
  ContactPopoverComponent,
}) => {
  const platformOptions = availablePlatforms
    ? PLATFORM_OPTIONS.filter((option) => availablePlatforms.includes(option.value))
    : PLATFORM_OPTIONS;
  return (
    <>
      {submitError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      {/* Platform Selection */}
      <div className="space-y-3">
        <Label htmlFor="platform" className="font-semibold">
          Calendar Platform
        </Label>
        <Select
          value={formData.platform}
          onValueChange={(value: CalendarPlatform) =>
            onInputChange("platform", value)
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {platformOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Meeting Title */}
      <div className="space-y-3">
        <Label htmlFor="title" className="font-semibold">
          Meeting Title *
        </Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => onInputChange("title", e.target.value)}
          placeholder="Enter meeting title"
          required
        />
      </div>

      {/* Description */}
      <div className="space-y-3">
        <Label htmlFor="description" className="font-semibold">
          Description
        </Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => onInputChange("description", e.target.value)}
          placeholder="Enter meeting description or agenda"
          rows={3}
          className="resize-none"
        />
      </div>

      {/* Date and Time */}
      <div className="space-y-4">
        <Label className="font-semibold block">Date & Time *</Label>
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label htmlFor="startDate">Date</Label>
            <Input
              id="startDate"
              type="date"
              value={formData.startDate}
              onChange={(e) => onInputChange("startDate", e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="startTime">Start Time</Label>
            <Input
              id="startTime"
              type="time"
              value={formData.startTime}
              onChange={(e) => onInputChange("startTime", e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="duration">Duration</Label>
            <Select
              value={formData.duration.toString()}
              onValueChange={(value: string) =>
                onInputChange("duration", parseInt(value))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value.toString()}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Meeting Type */}
      <div className="space-y-3">
        <Label className="font-semibold block">Meeting Type</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={formData.isOnline ? "default" : "outline"}
            onClick={() => onInputChange("isOnline", true)}
            className="flex-1 flex items-center gap-2"
          >
            <Video className="h-4 w-4" />
            Online
          </Button>
          <Button
            type="button"
            variant={!formData.isOnline ? "default" : "outline"}
            onClick={() => onInputChange("isOnline", false)}
            className="flex-1 flex items-center gap-2"
          >
            <MapPin className="h-4 w-4" />
            In-Person
          </Button>
        </div>

        {/* Online Meeting Notice */}
        {formData.isOnline && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Video className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Online Meeting Link</p>
                <p>
                  A meeting link will be automatically generated and included
                  in the calendar invite.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Location */}
      {!formData.isOnline && (
        <div className="space-y-3">
          <Label htmlFor="location" className="font-semibold">
            Location
          </Label>
          <Input
            id="location"
            value={formData.location}
            onChange={(e) => onInputChange("location", e.target.value)}
            placeholder="Enter meeting location"
          />
        </div>
      )}

      {/* Attendees */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="attendees" className="font-semibold">
            Attendees
          </Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onOpenAttendeeInfo}
            className="text-blue-600"
          >
            <Info className="h-4 w-4 mr-1" />
            Help
          </Button>
        </div>
        <div
          className="space-y-2"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {renderAttendeesSelector ? (
            <AttendeeSelector
              currentEmails={currentAttendees}
              onChange={onAttendeesChange}
              disabled={isSubmitting}
              externalSelector={renderAttendeesSelector}
            />
          ) : (
            <AttendeeSelectWithModal
              currentEmails={currentAttendees}
              onChange={onAttendeesChange}
              disabled={isSubmitting}
              placeholder="Search contacts or enter email addresses..."
              contacts={contacts}
              onSearchContacts={onSearchContacts}
              isLoadingContacts={isLoadingContacts}
            />
          )}

          {/* Quick Add Team Members */}
          <div className="space-y-2">
            <Label className="text-sm text-gray-600">
              Quick Add Team Members:
            </Label>
            <div className="flex flex-wrap gap-2">
              {TEAM_MEMBERS.map((member) => (
                <Button
                  key={member.email}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onAddTeamMember(member.email)}
                  disabled={currentAttendees.includes(member.email)}
                  className="text-xs"
                >
                  {member.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Current Attendees */}
          {currentAttendees.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">
                Current Attendees:
              </Label>
              <AttendeeChips
                emails={currentAttendees}
                onRemove={onRemoveAttendee}
                ContactPopoverComponent={ContactPopoverComponent}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
